import { Dialog, DialogBackdrop } from '@headlessui/react'
import mangoStore from '@store/mangoStore'
import clsx from 'clsx'
import Image from 'next/image'
import { ReactNode } from 'react'
import { IoMdClose } from 'react-icons/io'
import styles from './Modal.module.css'
import { CLOUD_FRONT_URL } from 'config/constants'
import { useViewport } from 'hooks/useViewport'
import { cn } from 'utils/classnames'

type ModalProps = {
  children: React.ReactNode
  disableOutsideClose?: boolean
  fullScreen?: boolean
  isOpen: boolean
  onClose?: () => void
  panelClassNames?: string
  hideClose?: boolean
  title?: string
  className?: string
  hasTitle?: boolean
  hasSubtitle?: boolean
  subTitle?: ReactNode
  hasImageAtBottom?: boolean
  isRegular?: boolean
  hasSpaceBtwCloseAndTitle?: boolean
  hideTitleBorder?: boolean
}

function MobileModal({
  children,
  disableOutsideClose = false,
  fullScreen = false,
  isOpen,
  onClose,
  panelClassNames,
  hideClose,
  title,
  className,
  hasTitle = true,
  hasSubtitle = false,
  subTitle,
  hasImageAtBottom = false,
  isRegular = false,
  hasSpaceBtwCloseAndTitle = true,
  hideTitleBorder = false,
}: ModalProps) {
  const themeData = mangoStore((s) => s.themeData)
  const { isMobile } = useViewport()

  const handleClose = () => {
    if (disableOutsideClose) {
      return
    }
    onClose ? onClose() : null
  }

  return (
    <Dialog
      open={isOpen}
      as="div"
      onClose={handleClose}
      className={`relative z-50 overflow-y-auto font-body ${className} text-xs`}
      title={title}
    >
      <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-[5px]" />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-black/40 backdrop-blur-[5px] ${
          disableOutsideClose ? 'pointer-events-none' : ''
        }`}
        aria-hidden="true"
      />
      <div className={`fixed inset-0 bottom-0 left-0 right-0 flex items-end`}>
        <Dialog.Panel
          className={`${themeData.fonts.body.variable} font-body ${
            fullScreen ? '' : !isRegular && styles.dialogueContainer
          } ${panelClassNames}`}
        >
          <div className={!isRegular ? styles.modalDarkBg : ''} />
          <div className={!isRegular ? styles.modalGlass : ''}>
            {!isRegular && (
              <div className="absolute z-[-1] h-full w-full">
                <Image
                  alt=""
                  src={CLOUD_FRONT_URL + '/images/bg-noise.png'}
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            )}
            <div className={!isRegular ? styles.modalBgNoise : ''}>
              {hasTitle && (
                <div
                  className={cn(
                    'border-b border-th-input-border pb-4',
                    'mx-4 mt-4 md:mx-6 md:mt-6',
                    hideTitleBorder && 'border-b-0',
                  )}
                >
                  <div
                    className={clsx(
                      'flex flex-col',
                      hasSpaceBtwCloseAndTitle && !isMobile ? 'space-y-4' : '',
                    )}
                  >
                    {!hideClose ? (
                      <button
                        onClick={onClose}
                        className={`self-end text-th-button focus:outline-none focus-visible:text-th-active sm:right-2 sm:top-2 md:hover:text-th-active`}
                      >
                        <IoMdClose fontSize={20} className="text-th-fgd-3" />
                      </button>
                    ) : null}
                    <Dialog.Title
                      as="h3"
                      className="text-th-fgd-1/90 text-2xl font-bold leading-8 sm:leading-6"
                    >
                      {title}
                    </Dialog.Title>
                  </div>
                  {hasSubtitle && (
                    <div className="text-xs font-normal leading-normal text-th-fgd-2 fhd:text-sm">
                      {subTitle}
                    </div>
                  )}
                </div>
              )}

              <div
                className={cn(
                  'h-full overflow-y-scroll',
                  'mx-4 mb-4 md:mx-6 md:mb-6',
                  hasImageAtBottom && '!mx-0 !mb-0',
                )}
              >
                {children}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default MobileModal
